<template>
  <div class="image-input">
    <div
      v-if="!uploading && !completed"
      class="image-input__dropzone"
      @click="$refs.fileInput.click()"
      @keydown.enter="$refs.fileInput.click()"
      @drop.prevent="handleFileDropped"
      :tabindex="0"
    >
      <div v-if="!uploading" class="image-input__dropzone__label text-center">
        <label :for="label">{{ label }}</label>
        <v-icon v-show="dragging">mdi-tray-arrow-down</v-icon>
        <v-icon v-show="!dragging">mdi-tray-arrow-up</v-icon>
      </div>

      <div
        v-else
        style="
          border-radius: 50%;
          display: flex;
          align-items: center;
          padding-left: 25px;
          padding-right: 25px;
          padding-top: 12px;
          padding-bottom: 12px;
        "
      >
        <v-progress-circular
          size="120"
          width="15"
          :value="progress"
          color="green"
          rounded
        >
          <strong>{{ progress }}%</strong>
        </v-progress-circular>
      </div>

      <div
        v-if="validationData.errors && validationData.errors.length > 0"
        class="v-text-field__details pl-2 pt-1"
      >
        <div class="v-messages theme--light error--text" role="alert">
          <div class="v-messages__wrapper">
            <div class="v-messages_message">
              <div
                v-for="(msg, index) in validationData.errors"
                :key="`error_${_uid}_${index}`"
              >
                {{ msg }}
              </div>
            </div>
          </div>
        </div>
      </div>

      <input
        :id="label"
        type="file"
        ref="fileInput"
        style="display: none"
        :accept="accept"
        @change="handleFileSelected"
      />
    </div>

    <template v-else>
      <div class="image-input__image-wrapper">
        <v-img
          class="round-image"
          :width="$vuetify.breakpoint.smAndDown ? 125 : 150"
          :height="$vuetify.breakpoint.smAndDown ? 125 : 150"
          :src="fileUrl"
        />
        <div class="image-input__image-wrpaper__actions">
          <v-btn plain icon @click="clearFile">
            <v-icon>mdi-delete</v-icon>
          </v-btn>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import { eventBus } from '@/event-bus.js';
import { mapGetters } from 'vuex';

import axios from 'axios';

export default {
  inject: {
    form: { default: null }
  },
  props: {
    value: {
      type: Object,
      required: false,
      default: () => null
    },

    label: {
      type: String,
      required: false,
      default: 'Hier klicken oder Datei ziehen zum Hochladen'
    },

    rules: {
      type: Array,
      required: false,
      default: () => []
    },

    uploadEndpoint: {
      type: String,
      required: false,
      default: ''
    },

    downloadEndpoint: {
      type: String,
      required: false,
      default: ''
    },

    additionalValues: {
      type: Object,
      required: false,
      default: () => {}
    },

    filePrefix: {
      type: String,
      required: false,
      default: ''
    },

    validationData: {
      type: Object,
      required: false,
      default: () => ({})
    }
  },

  data: () => ({
    dragging: false,
    uploading: false,
    completed: false,
    progress: 50,
    file: null,
    fileResult: null,
    shouldValidate: true,
    hasError: false,
    errorMessages: [],
    accept: 'image/*'
  }),

  computed: {
    ...mapGetters('companyModule', {
      apiUrl: 'currentCompanyApiUrl'
    }),

    fileUrl() {
      if (!this.fileResult) {
        return '';
      }

      return `${this.apiUrl}/${this.downloadEndpoint}?objectName=${this.fileResult.objectName}&contentType=${this.fileResult.contentType}`;
    }
  },

  watch: {
    file() {
      if (this.file) {
        this.uploadFile();
      }
    },

    fileResult() {
      this.$emit('input', this.fileResult);
    },

    value() {
      if (this.value) {
        this.fileResult = this.value;
        this.completed = true;
      }
    }
  },

  mounted() {
    if (this.value) {
      this.fileResult = this.value;
    }

    eventBus.$on('dragover', () => {
      this.dragging = true;
    });

    eventBus.$on('dragleave', () => {
      this.dragging = false;
    });

    eventBus.$on('dropped', () => {
      this.dragging = false;
    });
  },

  methods: {
    clearFile() {
      if (!this.fileResult) {
        return;
      }

      const url = `${this.apiUrl}/${this.uploadEndpoint}?objectName=${this.fileResult.objectName}`;
      axios
        .delete(url)
        .then(() => {
          this.fileResult = null;
          this.completed = false;
        })
        .catch(() => {
          eventBus.$emit(
            'errorMessage',
            'Das Löschen einer Date ist fehlgeschlagen.'
          );
        });
    },

    handleFileDropped(event) {
      eventBus.$emit('dropped');
      const files = event.dataTransfer.files;
      if (files.length > 0) {
        this.uploadFile(files[0]);
      }
    },

    handleFileSelected() {
      const files = this.$refs.fileInput.files;
      if (files.length > 0) {
        this.uploadFile(files[0]);
      }
    },

    uploadFile(file) {
      if (this.fileResult) {
        return;
      }

      this.uploading = true;
      this.progress = 0;
      const url = `${this.apiUrl}/${this.uploadEndpoint}`;

      const formData = new FormData();
      formData.append('file', file);
      formData.append('filePrefix', this.filePrefix);

      for (const key of Object.keys(this.additionalValues)) {
        formData.append(key, this.additionalValues[key]);
      }

      // in order to implement a progress bar when uploading a file we
      // need to use the axios package. The fetch api does not provide
      // the required functionality.
      axios
        .post(url, formData, {
          onUploadProgress: (progressEvent) => {
            const { loaded, total } = progressEvent;
            const percentage = Math.floor((loaded * 100) / total);
            this.progress = percentage;
          },
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        })
        .then(({ data }) => {
          data.fileName = file.name;
          this.fileResult = data;
          this.uploading = false;
          this.completed = true;
          this.$emit('fileUploaded');
        })
        .catch(() => {
          this.uploading = false;
          this.completed = false;
          this.fileResult = null;

          eventBus.$emit(
            'errorMessage',
            'Der Upload einer Datei ist fehlgeschlagen.'
          );
        });
    },

    async showFile(file) {
      if (!this.downloadEndpoint || this.downloadEndpoint === '') {
        return;
      }

      const url = `${this.apiUrl}/${this.downloadEndpoint}?objectName=${file.objectName}&contentType=${file.contentType}`;
      const { data } = await axios.get(url, { responseType: 'blob' });

      const blob = new Blob([data], { type: file.contentType });
      const fileUrl = window.URL.createObjectURL(blob);

      let tab = window.open();
      tab.location.href = fileUrl;

      window.URL.revokeObjectURL(fileUrl);
    },

    reset() {
      this.hasError = false;
      this.errorMessages = [];
      this.fileResult = null;
    }
  }
};
</script>
